import React from "react";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import PrayerForm from "../components/site/PrayerForm";
import UpdatesForm from "../components/site/UpdatesForm";
import Recordings from "../components/site/Recordings";
import Events from "../components/site/Events";
import Give from "../components/site/Give";
import Contact from "../components/site/Contact";
import About from "../components/site/About";
import Leadership from "../components/site/Leadership";
import Mentorship from "../components/site/Mentorship";
import FireHubs from "../components/site/FireHubs";
import SEO, { ORG_SCHEMA, breadcrumbSchema } from "../components/SEO";
import SocialFollow from "../components/SocialFollow";
import { EmbersBackdrop } from "../components/site/Atoms";

/**
 * Lightweight functional pages that mount an existing site section + full SEO.
 * One file. Configure the export per route.
 */
const makePage = ({ path, title, description, kicker, h1, intro, Component, schema = [] }) => () => (
  <>
    <SEO
      title={title}
      description={description}
      path={path}
      jsonLd={[
        ORG_SCHEMA,
        breadcrumbSchema([{ name: "Home", path: "/" }, { name: kicker, path }]),
        ...schema,
      ]}
    />
    <main className="relative" data-testid={`page-${path.replace(/\//g, "") || "root"}`}>
      <Navbar />
      <section className="relative pt-28 pb-12 px-6 overflow-hidden">
        <EmbersBackdrop density={8} />
        <div className="relative max-w-6xl mx-auto">
          <div className="overline mb-4">{kicker}</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-[#F6F0E6] leading-[1.05] max-w-3xl">{h1}</h1>
          {intro && <p className="mt-5 max-w-2xl text-lg text-[#D9D9D9]">{intro}</p>}
        </div>
      </section>
      <Component />
      <SocialFollow />
      <Footer />
    </main>
  </>
);

export const RequestPrayerPage = makePage({
  path: "/request-prayer",
  title: "Request Prayer | Revival Fire Ministries Global",
  description: "Submit a confidential prayer request to Revival Fire Ministries Global. Free, biblical, and Spirit-led prayer ministry for anyone, anywhere.",
  kicker: "Request Prayer",
  h1: "Request Prayer",
  intro: "Submit a confidential prayer request. Our intercessory team stands with you in the name of Jesus.",
  Component: PrayerForm,
  schema: [{
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Request Prayer",
    "url": "https://revivalfireministries.com/request-prayer",
  }],
});

export const JoinUpdatesPage = makePage({
  path: "/join-updates",
  title: "Join Updates | Revival Fire Ministries Global",
  description: "Receive ministry updates, prayer points, prophetic teaching, event announcements, and revival moments from Revival Fire Ministries Global.",
  kicker: "Join Updates",
  h1: "Stay connected to the fire",
  intro: "Get prayer updates, teaching, and revival announcements straight to your inbox.",
  Component: UpdatesForm,
});

export const ServiceRecordingsPage = makePage({
  path: "/service-recordings",
  title: "Service Recordings | Revival Fire Ministries Global",
  description: "Watch the latest service recordings from Revival Fire Ministries Global — prayer, teaching, prophetic ministry, healing, and deliverance services.",
  kicker: "Service Recordings",
  h1: "Service recordings",
  intro: "Watch the latest published services, prayer meetings, and revival gatherings.",
  Component: Recordings,
  schema: [{
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Service Recordings",
    "url": "https://revivalfireministries.com/service-recordings",
  }],
});

export const EventsPage = makePage({
  path: "/events",
  title: "Events | Revival Fire Ministries Global",
  description: "Upcoming tent revivals, crusades, prayer gatherings, healing services, deliverance services, and prophetic gatherings.",
  kicker: "Events",
  h1: "Upcoming gatherings",
  intro: "Find the next prayer meeting, revival service, or tent gathering near you — or online.",
  Component: Events,
});

export const GivePage = makePage({
  path: "/give",
  title: "Give | Revival Fire Ministries Global",
  description: "Partner with Revival Fire Ministries Global through generous giving. Support prayer ministry, prophetic mentorship, recordings, and revival gatherings.",
  kicker: "Give",
  h1: "Partner with the work of revival",
  intro: "Every gift fuels prayer, teaching, mentorship, and revival gatherings.",
  Component: Give,
});

export const ContactPage = makePage({
  path: "/contact",
  title: "Contact | Revival Fire Ministries Global",
  description: "Contact Revival Fire Ministries Global for pastoral care, mentorship inquiries, partnership, and ministry connections.",
  kicker: "Contact",
  h1: "Connect with the ministry",
  intro: "Reach out for pastoral care, mentorship, partnership, or ministry inquiries.",
  Component: Contact,
});

export const AboutPage = makePage({
  path: "/about",
  title: "About | Revival Fire Ministries Global",
  description: "About Revival Fire Ministries Global — a prophetic and apostolic holiness revival ministry calling hearts back to Jesus Christ.",
  kicker: "About",
  h1: "About Revival Fire Ministries Global",
  intro: "A prophetic and apostolic holiness revival ministry carrying the unadulterated Word of God with power.",
  Component: About,
});

export const LeadershipPage = makePage({
  path: "/leadership",
  title: "Leadership | Revival Fire Ministries Global",
  description: "Meet the leadership of Revival Fire Ministries Global — apostolic, prophetic, pastoral, and intercessory leaders carrying the fire.",
  kicker: "Leadership",
  h1: "Carrying the fire together",
  Component: Leadership,
});

export const FireHubsPage = makePage({
  path: "/fire-hubs",
  title: "Fire Hubs | Local Revival Gatherings",
  description: "Fire Hubs are local prayer and revival gatherings connected to Revival Fire Ministries Global.",
  kicker: "Fire Hubs",
  h1: "Fire Hubs — local revival in your city",
  Component: FireHubs,
});

export const MentorshipPage = makePage({
  path: "/prophetic-mentorship",
  title: "Prophetic Mentorship and Spiritual Covering",
  description: "Apply for prophetic mentorship and spiritual covering through Revival Fire Ministries Global.",
  kicker: "Prophetic Mentorship",
  h1: "Prophetic Mentorship",
  intro: "Be trained. Be sharpened. Be sent.",
  Component: Mentorship,
});
